import { inject, Injectable } from "@angular/core";
import { createBackendError, getBackendErrorMessage } from "../../../Common/Utility/UtilityError";
import { TestimonyApi } from "../Api/TestimonyApi";
import { TestimonyReportEntity } from "../Dto/TestimonyModel";
import { TestimonyFollowupRequest, TestimonySearchRequest } from "../Dto/TestimonySearchRequest";
import { TestimonyUpdateRequest } from "../Dto/TestimonyUpdateRequest";
import { TestimonyState } from "./TestimonyState";

@Injectable({ providedIn: "root" })
export class TestimonyEffects {
  private readonly testimonyApi = inject(TestimonyApi);
  private readonly testimonyState = inject(TestimonyState);

  async searchFollowups(params: TestimonyFollowupRequest = {}): Promise<void> {
    const merged = { page: 1, limit: 10, ...params };
    this.testimonyState.setLoading(true);
    try {
      const response = await this.testimonyApi.searchFollowups(merged);
      this.testimonyState.setFollowups(response.data, response.pagination, merged);
    } catch (exception: unknown) {
      this.testimonyState.setError(getBackendErrorMessage(exception));
    } finally {
      this.testimonyState.setLoading(false);
    }
  }

  async searchReports(params: TestimonySearchRequest = {}): Promise<void> {
    const merged = { page: 1, limit: 10, ...params };
    this.testimonyState.setLoading(true);
    try {
      const response = await this.testimonyApi.searchReports(merged);
      this.testimonyState.setReports(response.data, response.pagination, merged);
    } catch (exception: unknown) {
      this.testimonyState.setError(getBackendErrorMessage(exception));
    } finally {
      this.testimonyState.setLoading(false);
    }
  }

  async createFromRequest(requestId: string): Promise<TestimonyReportEntity> {
    try {
      return await this.testimonyApi.createFromRequest(requestId);
    } catch (exception: unknown) {
      this.testimonyState.setError(getBackendErrorMessage(exception));
      throw createBackendError(exception);
    }
  }

  async getReport(id: string): Promise<TestimonyReportEntity> {
    try {
      return await this.testimonyApi.getReport(id);
    } catch (exception: unknown) {
      this.testimonyState.setError(getBackendErrorMessage(exception));
      throw createBackendError(exception);
    }
  }

  async updateReport(id: string, payload: TestimonyUpdateRequest): Promise<TestimonyReportEntity> {
    try {
      return await this.testimonyApi.updateReport(id, payload);
    } catch (exception: unknown) {
      this.testimonyState.setError(getBackendErrorMessage(exception));
      throw createBackendError(exception);
    }
  }

  async updateStatus(id: string, payload: Parameters<TestimonyApi["updateStatus"]>[1]): Promise<TestimonyReportEntity> {
    try {
      return await this.testimonyApi.updateStatus(id, payload);
    } catch (exception: unknown) {
      this.testimonyState.setError(getBackendErrorMessage(exception));
      throw createBackendError(exception);
    }
  }

  async submitReport(id: string): Promise<TestimonyReportEntity> {
    try {
      return await this.testimonyApi.submitReport(id);
    } catch (exception: unknown) {
      this.testimonyState.setError(getBackendErrorMessage(exception));
      throw createBackendError(exception);
    }
  }
}
